import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  TextField,
  MenuItem,
  Typography,
  useTheme,
  useMediaQuery,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
} from '@mui/material';
import { Search, DateRange, Clear, Close, ArrowForward } from '@mui/icons-material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import { PickersDay, PickersDayProps } from '@mui/x-date-pickers/PickersDay';
import moment, { Moment } from 'moment';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '@/store/types';
import { fetchFeedTypes } from '@/store';
import { MyPostsList } from '@/components/my-posts/MyPostsList';

export const MyPostsPage: React.FC = () => {
  const theme = useTheme();
  const dispatch = useDispatch<AppDispatch>();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isSmallMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const { feedTypes, isLoading: feedTypesLoading, error } = useSelector(
    (state: RootState) => state.feed
  );

  const [search, setSearch] = useState('');
  const [selectedType, setSelectedType] = useState('all');
  const [startDate, setStartDate] = useState<Moment | null>(null);
  const [endDate, setEndDate] = useState<Moment | null>(null);
  const [selectedQuickFilter, setSelectedQuickFilter] = useState('');
  const [dateDialogOpen, setDateDialogOpen] = useState(false);
  const [tempStartDate, setTempStartDate] = useState<Moment | null>(null);
  const [tempEndDate, setTempEndDate] = useState<Moment | null>(null);

  // Pre-fetch feed types when page loads
  useEffect(() => {
    dispatch(fetchFeedTypes());
  }, [dispatch]);

  // Quick date filter options
  const quickDateFilters = [
    { label: 'Today', value: 'today' },
    { label: 'Yesterday', value: 'yesterday' },
    { label: 'This Week', value: 'this-week' },
    { label: 'Last Week', value: 'last-week' },
    // { label: 'This Month', value: 'this-month' },
    // { label: 'Last Month', value: 'last-month' },
    // { label: 'Last 7 Days', value: 'last-7-days' },
    // { label: 'Last 30 Days', value: 'last-30-days' },
  ];

  const getDateRange = (filter: string) => {
    const today = moment();
    
    switch (filter) {
      case 'today':
        return {
          start: today.clone(),
          end: today.clone()
        };
      
      case 'yesterday':
        const yesterday = today.clone().subtract(1, 'day');
        return {
          start: yesterday,
          end: yesterday.clone()
        };
      
      case 'this-week':
        return {
          start: today.clone().startOf('week'),
          end: today.clone()
        };
      
      case 'last-week':
        return {
          start: today.clone().subtract(1, 'week').startOf('week'),
          end: today.clone().subtract(1, 'week').endOf('week')
        };
      
      case 'this-month':
        return {
          start: today.clone().startOf('month'),
          end: today.clone()
        };
      
      case 'last-month':
        return {
          start: today.clone().subtract(1, 'month').startOf('month'),
          end: today.clone().subtract(1, 'month').endOf('month')
        };
      
      case 'last-7-days':
        return {
          start: today.clone().subtract(6, 'days'),
          end: today.clone()
        };
      
      case 'last-30-days':
        return {
          start: today.clone().subtract(29, 'days'),
          end: today.clone()
        };
      
      default:
        return { start: null, end: null };
    }
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(event.target.value);
  };

  const handleTypeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedType(event.target.value);
  };

  const handleQuickDateFilter = (filter: string) => {
    const { start, end } = getDateRange(filter);
    setTempStartDate(start);
    setTempEndDate(end);
    setSelectedQuickFilter(filter);
    
    // Apply immediately for quick filters
    setStartDate(start);
    setEndDate(end);
        setDateDialogOpen(false);
  };

  const openDateDialog = () => {
    setTempStartDate(startDate);
    setTempEndDate(endDate);
    setDateDialogOpen(true);
  };

  const closeDateDialog = () => {
    setDateDialogOpen(false);
    setSelectedQuickFilter('');
  };

  const applyDateFilter = () => {
    setStartDate(tempStartDate);
    setEndDate(tempEndDate);
    setDateDialogOpen(false);
    // API call will be triggered by useEffect in MyPostsList when startDate/endDate change
  };

  const clearDateFilters = () => {
    setStartDate(null);
    setEndDate(null);
    setSelectedQuickFilter('');
    setTempStartDate(null);
    setTempEndDate(null);
    setDateDialogOpen(false);
    // API call will be triggered by useEffect in MyPostsList when startDate/endDate change to empty
  };

  // Custom day component for range highlighting
  const CustomPickersDay = (props: PickersDayProps) => {
    const { day, ...other } = props;
    const dayMoment = day as Moment;

    if (!tempStartDate || !tempEndDate) {
      return <PickersDay {...props} />;
    }

    const isStart = dayMoment.isSame(tempStartDate, 'day');
    const isEnd = dayMoment.isSame(tempEndDate, 'day');
    const isInRange = dayMoment.isBetween(tempStartDate, tempEndDate, 'day', '[]');

    let dayStyle = {};
    
    if (isStart || isEnd) {
      dayStyle = {
        backgroundColor: 'primary.main',
        color: 'white',
        '&:hover': {
          backgroundColor: 'primary.dark',
        },
      };
    } else if (isInRange) {
      dayStyle = {
        backgroundColor: 'primary.lighter',
        color: 'primary.main',
        '&:hover': {
          backgroundColor: 'primary.light',
        },
      };
    }

    return (
      <PickersDay
        {...other}
        day={dayMoment}
        sx={dayStyle}
      />
    );
  };

  const getDateRangeLabel = () => {
    if (selectedQuickFilter) {
      return quickDateFilters.find(f => f.value === selectedQuickFilter)?.label || '';
    }
    if (startDate && endDate) {
      if (startDate.isSame(endDate, 'day')) {
        return startDate.format('MMM DD, YYYY');
      }
      return `${startDate.format('MMM DD, YYYY')} → ${endDate.format('MMM DD, YYYY')}`;
    }
    if (startDate) {
      return `From ${startDate.format('MMM DD, YYYY')}`;
    }
    if (endDate) {
      return `Until ${endDate.format('MMM DD, YYYY')}`;
    }
    return '';
  };

  return (
    <Box sx={{ 
      maxWidth: '100%', 
      mx: 'auto', 
      height: '100%',
      display: 'flex',
      flexDirection: 'column'
    }}>
      {/* Header */}
      <Box sx={{ mb: 3, flexShrink: 0 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          My Talky
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Manage and view all your talkies
        </Typography>
      </Box>

      {/* Search and Filter Section - Fixed/Sticky */}
      <Paper
        elevation={0}
        sx={{
          position: 'sticky',
          top: 10,
          zIndex: 1000,
          p: 3,
          mb: 3,
          borderRadius: 2,
          border: `1px solid ${theme.palette.divider}`,
          backgroundColor: theme.palette.background.paper,
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          flexShrink: 0,
        }}
      >
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          {/* Search Field */}
          <TextField
            size="small"
            placeholder="Search talkies..."
            value={search}
            onChange={handleSearchChange}
            sx={{ 
              minWidth: 300,
              flex: 1,
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
              },
            }}
            slotProps={{
              input: {
                startAdornment: <Search sx={{ color: 'text.secondary', mr: 1 }} />,
              },
            }}
          />

          {/* Type Filter */}
          <TextField
            select
            size="small"
            label="Type"
            value={selectedType}
            onChange={handleTypeChange}
            sx={{ 
              minWidth: 150,
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
              },
            }}
            disabled={feedTypesLoading || feedTypes?.length === 0}
          >
            <MenuItem value="all">All</MenuItem>
            {feedTypesLoading ? (
              <MenuItem disabled>
                <em>Loading types...</em>
              </MenuItem>
            ) : error ? (
              <MenuItem disabled>
                <em>Error loading types</em>
              </MenuItem>
            ) : feedTypes?.length === 0 ? (
              <MenuItem disabled>
                <em>No types available</em>
              </MenuItem>
            ) : (
              feedTypes?.map((type) => (
                <MenuItem key={type._id} value={type._id}>
                  {type.name.toLowerCase() === 'jobseeker' ? 'Hire me | Open to Work' : type.name.charAt(0).toUpperCase() + type.name.slice(1)}
                </MenuItem>
              ))
            )}
          </TextField>

          {/* Date Range Button */}
          <Button
            variant="outlined"
            onClick={openDateDialog}
            startIcon={<DateRange />}
            // sx={{
            //   minWidth: 200,
            //   textTransform: 'none',
            //   borderRadius: 2,
            //   justifyContent: 'flex-start',
            // }}
              sx={{
              textTransform: 'none',
              minWidth: 120,
              height:36,
              borderRadius:2
              // px: 9,
            }}
          >
            {getDateRangeLabel() || 'Select Date Range'}
          </Button>

          {/* Clear Date Filter */}
          {(startDate || endDate || selectedQuickFilter) && (
            <IconButton
              onClick={clearDateFilters}
              size="small"
              sx={{
                color: 'text.secondary',
                '&:hover': {
                  bgcolor: 'action.hover',
                },
              }}
            >
              <Clear />
            </IconButton>
          )}
        </Box>
      </Paper>

      {/* Posts List - Scrollable Area */}
      <Box sx={{ 
        flex: 1, 
        overflow: 'auto',
        minHeight: 0,
        '&::-webkit-scrollbar': {
          width: '6px',
        },
        '&::-webkit-scrollbar-track': {
          background: 'transparent',
        },
        '&::-webkit-scrollbar-thumb': {
          background: theme.palette.divider,
          borderRadius: '3px',
        },
        '&::-webkit-scrollbar-thumb:hover': {
          background: theme.palette.text.secondary,
        },
      }}>
        <MyPostsList 
          search={search} 
          type={selectedType} 
          startDate={startDate?.format('YYYY-MM-DD')}
          endDate={endDate?.format('YYYY-MM-DD')}
        />
      </Box>

      {/* Date Range Dialog */}
      <LocalizationProvider dateAdapter={AdapterMoment}>
        <Dialog 
          open={dateDialogOpen} 
          onClose={closeDateDialog}
          maxWidth={isMobile ? 'sm' : 'sm'}
          
          fullScreen={isSmallMobile}
          slotProps={{
            paper: { 
              sx: { 
                borderRadius: isSmallMobile ? 0 : 2,
                margin: isSmallMobile ? 0 : 2,
                maxHeight: isSmallMobile ? '100vh' : '80vh',
              } 
            }
          }}
        >
          <DialogTitle sx={{ pb: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box>
                {tempStartDate && tempEndDate ? (
                  <Typography variant="h6">
                    {tempStartDate.format('MMM DD, YYYY')} 
                    <ArrowForward sx={{ mx: 1, fontSize: 16 }} /> 
                    {tempEndDate.format('MMM DD, YYYY')}
                  </Typography>
                ) : (
                  <Typography variant="h6">Select Date Range</Typography>
                )}
              </Box>
              <IconButton onClick={closeDateDialog} size="small">
                <Close />
              </IconButton>
            </Box>
          </DialogTitle>
          <DialogContent sx={{ p: 0, overflow: 'auto', display:'flex', justifyContent:'center' }}>
            {/* <Box sx={{ 
              display: 'flex', 
              flexDirection: isMobile ? 'column' : 'row',
              height: isMobile ? 'auto' : 400,
              minHeight: isMobile ? '100%' : 400,
            }}> */}
              {/* Calendar Section */}
          {false &&   <Box sx={{ 
                flex: 1, 
                display: 'flex', 
                flexDirection: isMobile ? 'column' : 'row',
                borderRight: isMobile ? 0 : 1, 
                borderBottom: isMobile ? 1 : 0,
                borderColor: 'divider' 
              }}>
                {/* Start Date Calendar */}
                <Box sx={{ 
                  flex: 1, 
                  p: isMobile ? 1 : 2,
                  borderBottom: isMobile ? 1 : 0,
                  borderColor: 'divider'
                }}>
                  {isMobile && (
                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, textAlign: 'center' }}>
                      Start Date
                    </Typography>
                  )}
                  <DateCalendar
                    value={tempStartDate}
                    onChange={(newValue) => {
                      setTempStartDate(newValue);
                      setSelectedQuickFilter('');
                      if (newValue && (!tempEndDate || newValue.isAfter(tempEndDate))) {
                        setTempEndDate(newValue);
                      }
                    }}
                    minDate={moment().subtract(5, 'years')}
                    maxDate={moment()}
                    views={['year', 'month', 'day']}
                    openTo="day"
                    slots={{
                      day: CustomPickersDay,
                    }}
                    shouldDisableDate={(date) => date.isAfter(moment(), 'day')}
                    sx={{
                      width: '100%',
                      '& .MuiPickersDay-root': {
                        fontSize: isMobile ? '0.75rem' : '0.875rem',
                        width: isMobile ? 32 : 36,
                        height: isMobile ? 32 : 36,
                      },
                      '& .MuiPickersCalendarHeader-root': {
                        paddingLeft: isMobile ? 0.5 : 1,
                        paddingRight: isMobile ? 0.5 : 1,
                      },
                      '& .MuiPickersCalendarHeader-label': {
                        fontSize: isMobile ? '0.875rem' : '1rem',
                        cursor: 'pointer',
                        '&:hover': {
                          backgroundColor: 'action.hover',
                        },
                      },
                      '& .MuiPickersYear-yearButton': {
                        fontSize: isMobile ? '0.75rem' : '0.875rem',
                      },
                      '& .MuiPickersMonth-monthButton': {
                        fontSize: isMobile ? '0.75rem' : '0.875rem',
                      },
                      '& .MuiDayCalendar-weekDayLabel': {
                        fontSize: isMobile ? '0.75rem' : '0.875rem',
                      },
                    }}
                  />
                </Box>
                
                {/* End Date Calendar */}
                <Box sx={{ 
                  flex: 1, 
                  p: isMobile ? 1 : 2, 
                  borderLeft: isMobile ? 0 : 1, 
                  borderColor: 'divider' 
                }}>
                  {isMobile && (
                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, textAlign: 'center' }}>
                      End Date
                    </Typography>
                  )}
                  <DateCalendar
                    value={tempEndDate}
                    onChange={(newValue) => {
                      setTempEndDate(newValue);
                      setSelectedQuickFilter('');
                    }}
                    minDate={tempStartDate || moment().subtract(5, 'years')}
                    maxDate={moment()}
                    views={['year', 'month', 'day']}
                    openTo="day"
                    slots={{
                      day: CustomPickersDay,
                    }}
                    shouldDisableDate={(date) => date.isAfter(moment(), 'day')}
                    sx={{
                      width: '100%',
                      '& .MuiPickersDay-root': {
                        fontSize: isMobile ? '0.75rem' : '0.875rem',
                        width: isMobile ? 32 : 36,
                        height: isMobile ? 32 : 36,
                      },
                      '& .MuiPickersCalendarHeader-root': {
                        paddingLeft: isMobile ? 0.5 : 1,
                        paddingRight: isMobile ? 0.5 : 1,
                      },
                      '& .MuiPickersCalendarHeader-label': {
                        fontSize: isMobile ? '0.875rem' : '1rem',
                        cursor: 'pointer',
                        '&:hover': {
                          backgroundColor: 'action.hover',
                        },
                      },
                      '& .MuiPickersYear-yearButton': {
                        fontSize: isMobile ? '0.75rem' : '0.875rem',
                      },
                      '& .MuiPickersMonth-monthButton': {
                        fontSize: isMobile ? '0.75rem' : '0.875rem',
                      },
                      '& .MuiDayCalendar-weekDayLabel': {
                        fontSize: isMobile ? '0.75rem' : '0.875rem',
                      },
                    }}
                  />
                </Box>
              </Box>}

              {/* Quick Filters Section */}
              <Box sx={{ 
                width: isMobile ? '100%' : 200, 
                p: isMobile ? 2 : 2, 

                bgcolor: theme.palette.mode === 'dark' ? 'grey.900' : 'grey.50',
                borderTop: isMobile ? 1 : 0,
                borderColor: 'divider'
              }}>
                <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
                  Quick Filters
                </Typography>
                <Box sx={{ 
                  display: 'flex', 
                  flexDirection: isMobile ? 'row' : 'column', 
                  flexWrap: isMobile ? 'wrap' : 'nowrap',
                  gap: 1 
                }}>
                  {quickDateFilters.map((filter) => (
                    <Button
                      key={filter.value}
                      onClick={() => handleQuickDateFilter(filter.value)}
                      variant={selectedQuickFilter === filter.value ? 'contained' : 'text'}
                      size="small"
                      sx={{
                        justifyContent: 'flex-start',
                        textTransform: 'none',
                        fontSize: isMobile ? '0.75rem' : '0.875rem',
                        minWidth: isMobile ? 'auto' : 'unset',
                        flex: isMobile ? '1 1 auto' : 'none',
                        color: selectedQuickFilter === filter.value ? 'white' : 'text.primary',
                        bgcolor: selectedQuickFilter === filter.value ? 'primary.main' : 'transparent',
                        '&:hover': {
                          bgcolor: selectedQuickFilter === filter.value ? 'primary.dark' : 'action.hover',
                        },
                      }}
                    >
                      {filter.label}
                    </Button>
                  ))}
                </Box>
              </Box>
            {/* </Box> */}
          </DialogContent>
          <DialogActions sx={{ 
            px: isMobile ? 2 : 3, 
            py: 2,
            flexDirection: isSmallMobile ? 'column' : 'row',
            gap: isSmallMobile ? 1 : 0
          }}>
            <Button 
              onClick={clearDateFilters} 
              color="error"
              fullWidth={isSmallMobile}
              size={isMobile ? 'medium' : 'small'}
            >
              Clear All
            </Button>
            <Button 
              onClick={closeDateDialog}
              fullWidth={isSmallMobile}
              size={isMobile ? 'medium' : 'small'}
            >
              Cancel
            </Button>
            <Button 
              onClick={applyDateFilter} 
              variant="contained"
              disabled={!tempStartDate && !tempEndDate && !selectedQuickFilter}
              fullWidth={isSmallMobile}
              size={isMobile ? 'medium' : 'small'}
            >
              Apply Filter
            </Button>
          </DialogActions>
        </Dialog>
      </LocalizationProvider>
    </Box>
  );
};